import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import {
  CandlestickSeries,
  ColorType,
  CrosshairMode,
  HistogramSeries,
  LineSeries,
  createChart,
  createSeriesMarkers,
  type CandlestickData,
  type HistogramData,
  type IChartApi,
  type ISeriesApi,
  type ISeriesMarkersPluginApi,
  type LineData,
  type Logical,
  type LogicalRange,
  type Time,
  type UTCTimestamp,
} from "lightweight-charts";

import { buildExtremaMarkers } from "../chartExtrema";
import { formatCompact, formatPrice } from "../format";
import { prependedItemCount } from "../history";
import type { IndicatorPoint, PriceBar } from "../types";

interface MarketChartProps {
  bars: PriceBar[];
  indicators: IndicatorPoint[];
  loading: boolean;
  historyLoading: boolean;
  hasMoreHistory: boolean;
  onLoadOlder: () => void;
}

const MA_SERIES = [
  ["ma5", "#ff8a00"],
  ["ma10", "#1683ff"],
  ["ma20", "#c52baa"],
  ["ma30", "#00afc7"],
  ["ma60", "#f04f13"],
] as const;
const HISTORY_TRIGGER_BARS = 24;
type MaField = (typeof MA_SERIES)[number][0];

interface PriceSeriesState {
  candles: ISeriesApi<"Candlestick", Time>;
  volume: ISeriesApi<"Histogram", Time>;
  movingAverages: Record<MaField, ISeriesApi<"Line", Time>>;
  extremaMarkers: ISeriesMarkersPluginApi<Time>;
}

interface MacdSeriesState {
  histogram: ISeriesApi<"Histogram", Time>;
  dif: ISeriesApi<"Line", Time>;
  dea: ISeriesApi<"Line", Time>;
}

function chartTime(value: string): UTCTimestamp {
  return Math.floor(new Date(value).getTime() / 1000) as UTCTimestamp;
}

function rangesMatch(
  current: LogicalRange | null,
  next: LogicalRange,
): boolean {
  return current?.from === next.from && current.to === next.to;
}

export function MarketChart({
  bars,
  indicators,
  loading,
  historyLoading,
  hasMoreHistory,
  onLoadOlder,
}: MarketChartProps) {
  const priceContainer = useRef<HTMLDivElement>(null);
  const macdContainer = useRef<HTMLDivElement>(null);
  const priceChart = useRef<IChartApi | null>(null);
  const macdChart = useRef<IChartApi | null>(null);
  const priceSeries = useRef<PriceSeriesState | null>(null);
  const macdSeries = useRef<MacdSeriesState | null>(null);
  const candleDataRef = useRef<CandlestickData<UTCTimestamp>[]>([]);
  const visibleRange = useRef<LogicalRange | null>(null);
  const synchronizingRange = useRef(false);
  const previousFirstBarTime = useRef<string | null>(null);
  const dataInitialized = useRef(false);
  const historyLoadingRef = useRef(historyLoading);
  const hasMoreHistoryRef = useRef(hasMoreHistory);
  const onLoadOlderRef = useRef(onLoadOlder);

  const latestBar = bars.at(-1);
  const latestIndicator = indicators.at(-1);
  const turnover =
    latestBar?.close && latestBar.volume
      ? latestBar.close * latestBar.volume
      : null;

  const indicatorByTime = useMemo(
    () =>
      new Map(
        indicators.map((point) => [chartTime(point.observed_at), point] as const),
      ),
    [indicators],
  );

  useEffect(() => {
    historyLoadingRef.current = historyLoading;
    hasMoreHistoryRef.current = hasMoreHistory;
    onLoadOlderRef.current = onLoadOlder;
  }, [hasMoreHistory, historyLoading, onLoadOlder]);

  useLayoutEffect(() => {
    const priceElement = priceContainer.current;
    const macdElement = macdContainer.current;
    if (!priceElement || !macdElement) return;

    const price = createChart(priceElement, {
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: "#ffffff" },
        textColor: "#4e5b63",
        fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
        fontSize: 11,
      },
      grid: {
        vertLines: { color: "#edf0f2" },
        horzLines: { color: "#e6eaed" },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: {
        borderColor: "#cfd6da",
        scaleMargins: { top: 0.06, bottom: 0.24 },
      },
      timeScale: {
        borderColor: "#cfd6da",
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 3,
      },
    });

    const candles = price.addSeries(CandlestickSeries, {
      upColor: "#e73d43",
      downColor: "#2f9e55",
      borderUpColor: "#e73d43",
      borderDownColor: "#2f9e55",
      wickUpColor: "#e73d43",
      wickDownColor: "#2f9e55",
      priceLineVisible: true,
      lastValueVisible: true,
    });
    const extremaMarkers = createSeriesMarkers(candles, [], {
      autoScale: true,
      zOrder: "top",
    });

    const volume = price.addSeries(HistogramSeries, {
      priceScaleId: "volume",
      priceFormat: { type: "volume" },
      lastValueVisible: false,
      priceLineVisible: false,
    });
    price.priceScale("volume").applyOptions({
      scaleMargins: { top: 0.8, bottom: 0 },
      borderVisible: false,
    });

    const movingAverages = {} as Record<
      MaField,
      ISeriesApi<"Line", Time>
    >;
    for (const [field, color] of MA_SERIES) {
      movingAverages[field] = price.addSeries(LineSeries, {
        color,
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false,
      });
    }

    const macd = createChart(macdElement, {
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: "#ffffff" },
        textColor: "#4e5b63",
        fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
        fontSize: 11,
      },
      grid: {
        vertLines: { color: "#edf0f2" },
        horzLines: { color: "#e6eaed" },
      },
      rightPriceScale: {
        borderColor: "#cfd6da",
        scaleMargins: { top: 0.12, bottom: 0.1 },
      },
      timeScale: {
        borderColor: "#cfd6da",
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 3,
      },
    });
    const histogram = macd.addSeries(HistogramSeries, {
      priceLineVisible: false,
      lastValueVisible: false,
    });
    const dif = macd.addSeries(LineSeries, {
      color: "#ff7a00",
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    });
    const dea = macd.addSeries(LineSeries, {
      color: "#1683ff",
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    });

    let historyLoadArmed = false;
    const historyArmTimer = window.setTimeout(() => {
      historyLoadArmed = true;
    }, 250);
    const maybeLoadOlder = (range: LogicalRange) => {
      if (
        historyLoadArmed &&
        range.from <= HISTORY_TRIGGER_BARS &&
        hasMoreHistoryRef.current &&
        !historyLoadingRef.current
      ) {
        onLoadOlderRef.current();
      }
    };

    const syncMacdRange = (range: LogicalRange | null) => {
      if (!range) return;
      priceSeries.current?.extremaMarkers.setMarkers(
        buildExtremaMarkers(candleDataRef.current, range),
      );
      if (synchronizingRange.current) return;
      visibleRange.current = range;
      maybeLoadOlder(range);
      const target = macdChart.current;
      if (
        !target ||
        rangesMatch(target.timeScale().getVisibleLogicalRange(), range)
      ) {
        return;
      }
      synchronizingRange.current = true;
      target.timeScale().setVisibleLogicalRange(range);
      synchronizingRange.current = false;
    };
    const syncPriceRange = (range: LogicalRange | null) => {
      if (!range || synchronizingRange.current) return;
      visibleRange.current = range;
      maybeLoadOlder(range);
      const target = priceChart.current;
      if (
        !target ||
        rangesMatch(target.timeScale().getVisibleLogicalRange(), range)
      ) {
        return;
      }
      synchronizingRange.current = true;
      target.timeScale().setVisibleLogicalRange(range);
      synchronizingRange.current = false;
    };
    price
      .timeScale()
      .subscribeVisibleLogicalRangeChange(syncMacdRange);
    macd
      .timeScale()
      .subscribeVisibleLogicalRangeChange(syncPriceRange);

    priceChart.current = price;
    macdChart.current = macd;
    priceSeries.current = {
      candles,
      volume,
      movingAverages,
      extremaMarkers,
    };
    macdSeries.current = { histogram, dif, dea };

    const priceObserver = new ResizeObserver(() =>
      price.applyOptions({ autoSize: true }),
    );
    const macdObserver = new ResizeObserver(() =>
      macd.applyOptions({ autoSize: true }),
    );
    priceObserver.observe(priceElement);
    macdObserver.observe(macdElement);
    return () => {
      price
        .timeScale()
        .unsubscribeVisibleLogicalRangeChange(syncMacdRange);
      macd
        .timeScale()
        .unsubscribeVisibleLogicalRangeChange(syncPriceRange);
      window.clearTimeout(historyArmTimer);
      priceObserver.disconnect();
      macdObserver.disconnect();
      priceChart.current = null;
      macdChart.current = null;
      priceSeries.current = null;
      macdSeries.current = null;
      candleDataRef.current = [];
      previousFirstBarTime.current = null;
      visibleRange.current = null;
      dataInitialized.current = false;
      price.remove();
      macd.remove();
    };
  }, []);

  useLayoutEffect(() => {
    const price = priceChart.current;
    const macd = macdChart.current;
    const priceState = priceSeries.current;
    const macdState = macdSeries.current;
    if (!price || !macd || !priceState || !macdState) return;

    if (bars.length === 0) {
      priceState.candles.setData([]);
      priceState.volume.setData([]);
      priceState.extremaMarkers.setMarkers([]);
      for (const series of Object.values(priceState.movingAverages)) {
        series.setData([]);
      }
      macdState.histogram.setData([]);
      macdState.dif.setData([]);
      macdState.dea.setData([]);
      candleDataRef.current = [];
      previousFirstBarTime.current = null;
      visibleRange.current = null;
      dataInitialized.current = false;
      return;
    }

    const rangeBeforeUpdate =
      price.timeScale().getVisibleLogicalRange() ?? visibleRange.current;
    const prepended = prependedItemCount(
      previousFirstBarTime.current,
      bars,
      (bar) => bar.open_time,
    );
    const candleData: CandlestickData<UTCTimestamp>[] = bars
      .filter(
        (bar) =>
          bar.open !== null &&
          bar.high !== null &&
          bar.low !== null &&
          bar.close !== null,
      )
      .map((bar) => ({
        time: chartTime(bar.open_time),
        open: bar.open as number,
        high: bar.high as number,
        low: bar.low as number,
        close: bar.close as number,
      }));
    candleDataRef.current = candleData;
    priceState.candles.setData(candleData);

    const volumeData: HistogramData<UTCTimestamp>[] = bars.flatMap(
      (bar, index) =>
        bar.volume === null || bar.close === null
          ? []
          : [
              {
                time: chartTime(bar.open_time),
                value: bar.volume,
                color:
                  index === 0 ||
                  bar.close >= (bars[index - 1]?.close ?? bar.close)
                    ? "rgba(231, 61, 67, 0.72)"
                    : "rgba(47, 158, 85, 0.72)",
              },
            ],
    );
    priceState.volume.setData(volumeData);

    for (const [field] of MA_SERIES) {
      const data: LineData<UTCTimestamp>[] = candleData.flatMap((item) => {
        const value = indicatorByTime.get(item.time)?.[field];
        return value === null || value === undefined
          ? []
          : [{ time: item.time, value }];
      });
      priceState.movingAverages[field].setData(data);
    }

    macdState.histogram.setData(
      indicators.map((point) => ({
        time: chartTime(point.observed_at),
        value: point.display_bar,
        color:
          point.display_bar >= 0
            ? "rgba(231, 61, 67, 0.78)"
            : "rgba(47, 158, 85, 0.78)",
      })),
    );
    macdState.dif.setData(
      indicators.map((point) => ({
        time: chartTime(point.observed_at),
        value: point.dif,
      })),
    );
    macdState.dea.setData(
      indicators.map((point) => ({
        time: chartTime(point.observed_at),
        value: point.dea,
      })),
    );

    if (!dataInitialized.current) {
      price.timeScale().fitContent();
      const initialRange = price.timeScale().getVisibleLogicalRange();
      if (initialRange) {
        macd.timeScale().setVisibleLogicalRange(initialRange);
        visibleRange.current = initialRange;
      }
      dataInitialized.current = true;
    } else if (prepended > 0 && rangeBeforeUpdate) {
      const shiftedRange = {
        from: (rangeBeforeUpdate.from + prepended) as Logical,
        to: (rangeBeforeUpdate.to + prepended) as Logical,
      };
      price.timeScale().setVisibleLogicalRange(shiftedRange);
      macd.timeScale().setVisibleLogicalRange(shiftedRange);
      visibleRange.current = shiftedRange;
    } else {
      visibleRange.current = price.timeScale().getVisibleLogicalRange();
    }

    priceState.extremaMarkers.setMarkers(
      buildExtremaMarkers(
        candleData,
        price.timeScale().getVisibleLogicalRange(),
      ),
    );
    previousFirstBarTime.current = bars[0]?.open_time ?? null;
  }, [bars, indicatorByTime, indicators]);

  return (
    <div className="market-chart" data-testid="market-chart">
      <div className="chart-legend">
        <div className="legend-main">
          <span className="legend-label">PRICE</span>
          <strong>{formatPrice(latestBar?.close)}</strong>
          <span>VOL {formatCompact(latestBar?.volume)}</span>
          <span>AMT {formatCompact(turnover)}</span>
          {latestBar && !latestBar.complete && (
            <span className="live-candle">FORMING</span>
          )}
          {historyLoading && (
            <span className="history-loading">LOADING EARLIER</span>
          )}
        </div>
        <div className="ma-legend">
          {MA_SERIES.map(([field, color]) => (
            <span key={field} style={{ color }}>
              {field.toUpperCase()} {formatPrice(latestIndicator?.[field])}
            </span>
          ))}
        </div>
      </div>
      <div className="price-chart-shell">
        <div ref={priceContainer} className="price-chart-canvas" />
        {loading && <div className="chart-state">Loading market bars…</div>}
        {!loading && bars.length === 0 && (
          <div className="chart-state">Waiting for cached market bars</div>
        )}
      </div>
      <div className="macd-header">
        <span>MACD (12, 26, 9)</span>
        <span className="macd-hist">
          MACD {formatPrice(latestIndicator?.display_bar)}
        </span>
        <span className="macd-dif">
          DIF {formatPrice(latestIndicator?.dif)}
        </span>
        <span className="macd-dea">
          DEA {formatPrice(latestIndicator?.dea)}
        </span>
        <span className="raw-label">RAW CLOSE</span>
      </div>
      <div className="macd-chart-shell">
        <div ref={macdContainer} className="macd-chart-canvas" />
        {!loading && indicators.length === 0 && (
          <div className="chart-state compact">Calculating indicator history</div>
        )}
      </div>
    </div>
  );
}
